// src/components/layout/Topbar.jsx
import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "auth/AuthProvider";
import { Button } from "components/ui/Button";
import { ThemeToggle } from "components/ui/ThemeToggle";

export const Topbar = () => {
  const { profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  const navItem =
    "px-3 py-2 rounded-lg text-sm font-medium transition hover:bg-slate-100/70 dark:hover:bg-slate-700/70";
  const active = "bg-slate-900 text-white hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-700";

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:border-slate-700/70 dark:bg-slate-800/70 dark:supports-[backdrop-filter]:bg-slate-800/60">
      {/* верхняя тонкая линия-акцент */}
      <div className="h-0.5 bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500" />

      <div className="mx-auto max-w-7xl px-3 sm:px-6">
        <div className="flex h-14 items-center justify-between gap-3">
          {/* Лого/название + бургер */}
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="hidden sm:block font-semibold text-slate-900 dark:text-slate-100">{profile?.company_name || "Sklad App"}</span>
            </Link>

            {/* Burger (mobile) */}
            <button
              type="button"
              className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200/70 bg-white/80 text-slate-700 hover:bg-white sm:hidden dark:border-slate-700/70 dark:bg-slate-800/80 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Menyuni ochish"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? (
                // close
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                // burger
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Навигация (desktop) */}
          <nav className="hidden sm:flex items-center gap-1">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Bosh sahifa
            </NavLink>
            <NavLink
              to="/items"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Mahsulotlar
            </NavLink>
            <NavLink
              to="/suppliers"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Yetkazib beruvchilar
            </NavLink>
            <NavLink
              to="/assignments"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Topshiriqlar
            </NavLink>
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Foydalanuvchilar
            </NavLink>
            <NavLink
              to="/acts"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Dalolatnomalar
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `${navItem} ${isActive ? active : "text-slate-700 dark:text-slate-200"}`
              }
            >
              Sozlamalar
            </NavLink>
          </nav>

          {/* Профиль + тема + выход */}
          <div className="flex items-center gap-2">
            <div className="hidden text-sm sm:flex items-center gap-2 rounded-xl border border-slate-200/70 bg-white/70 px-3 py-1.5 dark:border-slate-700/70 dark:bg-slate-800/70">
              <div className="h-6 w-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500" />
              <span className="whitespace-nowrap text-slate-900 dark:text-slate-100">
                {profile?.full_name ?? "—"}{" "}
                <span className="opacity-60">({profile?.role ?? "—"})</span>
              </span>
            </div>

            {/* Theme Toggle */}
            <ThemeToggle />

            <Button variant="outline" onClick={signOut}>
              Chiqish
            </Button>
          </div>
        </div>
      </div>

      {/* Навигация (mobile dropdown) */}
      {open && (
        <div className="sm:hidden border-t border-slate-200/70 bg-white/90 dark:border-slate-700/70 dark:bg-slate-800/90">
          <nav className="mx-auto max-w-7xl px-3 py-3 grid gap-2">
            <MobileLink to="/dashboard" onClick={() => setOpen(false)}>
              Bosh sahifa
            </MobileLink>
            <MobileLink to="/items" onClick={() => setOpen(false)}>
              Mahsulotlar
            </MobileLink>
            <MobileLink to="/suppliers" onClick={() => setOpen(false)}>
              Yetkazib beruvchilar
            </MobileLink>
            <MobileLink to="/assignments" onClick={() => setOpen(false)}>
              Topshiriqlar
            </MobileLink>
            <MobileLink to="/users" onClick={() => setOpen(false)}>
              Foydalanuvchilar
            </MobileLink>
            <MobileLink to="/acts" onClick={() => setOpen(false)}>
              Dalolatnomalar
            </MobileLink>
            <MobileLink to="/settings" onClick={() => setOpen(false)}>
              Sozlamalar
            </MobileLink>
            <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200/70 bg-white px-3 py-2 text-sm dark:border-slate-700/70 dark:bg-slate-800">
              <span className="text-slate-900 dark:text-slate-100">
                {profile?.full_name ?? "—"}{" "}
                <span className="opacity-60">({profile?.role ?? "—"})</span>
              </span>
              <Button variant="outline" onClick={signOut}>
                Chiqish
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

const MobileLink = ({ to, children, onClick }) => {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `block w-full rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? "bg-slate-900 text-white dark:bg-slate-700" : "hover:bg-slate-100 text-slate-700 dark:hover:bg-slate-700 dark:text-slate-200"
        }`
      }
    >
      {children}
    </NavLink>
  );
};

